<script setup lang="ts">
/**
 * LocaleSwitcher — bascule FR / EN. Segmented control minimal aligné sur
 * le design system « Encre & Olivine » (font-mono, tracking-wider).
 * Persiste le choix via setLocale (localStorage + <html lang>).
 */
import { useI18n } from 'vue-i18n'

import { SUPPORTED_LOCALES, setLocale, type AppLocale } from '@/infrastructure/i18n'

const { locale } = useI18n()

function choose(next: AppLocale): void {
    if (locale.value !== next) {
        setLocale(next)
    }
}
</script>

<template>
    <div class="locale-switcher" role="group" aria-label="Language">
        <button
            v-for="loc in SUPPORTED_LOCALES"
            :key="loc"
            type="button"
            class="locale-switcher__btn"
            :class="{ 'locale-switcher__btn--active': locale === loc }"
            :aria-pressed="locale === loc"
            @click="choose(loc)"
        >
            {{ loc.toUpperCase() }}
        </button>
    </div>
</template>

<style scoped>
.locale-switcher {
    display: inline-flex;
    gap: 1px;
    border: 1px solid var(--p-color-line);
    border-radius: var(--p-radius-sm, 4px);
    overflow: hidden;
}
.locale-switcher__btn {
    appearance: none;
    border: 0;
    background: transparent;
    cursor: pointer;
    padding: 2px 8px;
    font-family: var(--p-font-mono);
    font-size: 10px;
    letter-spacing: var(--p-tracking-wider);
    color: var(--p-color-ink-4);
    transition: background 0.12s ease, color 0.12s ease;
}
.locale-switcher__btn:hover {
    color: var(--p-color-ink);
}
.locale-switcher__btn--active {
    background: var(--p-color-ink);
    color: var(--p-color-paper, #fff);
}
</style>
